import React from 'react'
import { merge } from 'lodash'
import * as SM from 'simple-markdown'
import { cleanTextValue } from 'ytil'
import config from './config'
import { ImageNode, VariableNode, VimeoNode, YouTubeNode } from './types'
import VimeoPlayer from './VimeoPlayer'
import YouTubePlayer from './YouTubePlayer'

const YOUTUBE_REG_EXP = /^https:\/\/[^/]*(?:youtube\.com|youtu\.be)\/watch\?v=(.+)/i
const VIMEO_REG_EXP   = /^https:\/\/[^/]*vimeo\.com\/(\d+)/i

export default function buildRules() {
  return merge({}, SM.defaultRules, {
    image: {
      parse: (capture: RegExpExecArray, parse: SM.Parser, state: SM.State): ImageNode => {
        let fullWidth = false
        let caption   = cleanTextValue(capture[1], true)
        if (caption != null && caption[0] === '[' && caption[caption.length - 1] === ']') {
          caption = caption?.slice(1, -1)
          fullWidth = true
        }

        return {
          type:   'image',
          target: capture[2],
          alt:    caption ?? '',
          title:  caption,
          style:  {fullWidth},
        }
      },

      react: (node: ImageNode, output: SM.Output<any>, state: SM.State) => {
        const {target, alt, title, style} = node
        const classNames = {fullWidth: style.fullWidth}

        return (
          <figure key={state.key} classNames={classNames} title={title}>
            <img src={target} alt={alt}/>
          </figure>
        )
      },
    },

    variable: {
      order: SM.defaultRules.inlineCode.order,

      match: (source: string) => /^\{\{(.*?)\}\}/.exec(source),

      parse: (capture: SM.Capture, parse: any, state: SM.State) => {
        return {
          type:     'variable',
          variable: capture[1],
        }
      },

      react: (node: VariableNode, output: SM.Output<any>, state: SM.State) => {
        return (
          <var key={state.key}>
            {`{{${node.variable}}}`}
          </var>
        )
      },
    },

    youtube: {
      order: SM.defaultRules.url.order - 1,

      match: (source: string) => YOUTUBE_REG_EXP.exec(source),

      parse: (capture: SM.Capture, parse: any, state: SM.State) => {
        return {
          type:    'youtube',
          videoID: capture[1],
          url:     capture[0],
        }
      },

      react: (node: YouTubeNode, output: SM.Output<any>, state: SM.State) => {
        return (
          <YouTubePlayer
            key={state.key}
            videoId={node.videoID}
          />
        )
      },
    },

    vimeo: {
      order: SM.defaultRules.url.order - 1,

      match: (source: string) => VIMEO_REG_EXP.exec(source),

      parse: (capture: SM.Capture, parse: any, state: SM.State) => {
        return {
          type:    'vimeo',
          videoID: capture[1],
          url:     capture[0],
        }
      },

      react: (node: VimeoNode, output: SM.Output<any>, state: SM.State) => {
        return (
          <VimeoPlayer
            key={state.key}
            video={node.videoID}
          />
        )
      },
    },

  }, config.extraRules)
}
